import React, { useRef, useState } from 'react';
import html2pdf from 'html2pdf.js';
import { savePDFForm } from './supabaseService';

const LOGO_URL = '/logo.jpg';

const initialState = {
  ground: {
    canteenFloor: false,
    tables: false,
    doors: false,
    chairs: false,
    windows: false,
    surfaces: false, // kitchen surfaces
    microwave: false,
    binsEmptied: false,
    toiletsMens: false,
    toiletsWomens: false,
    dryingRoom: false, // changed from drawing room
    toiletRollsRefill: false,
    handwashRefill: false,
  },
  middle: {
    officeFloor: false,
    meetingRooms: false, // changed from meeting room
    toiletsMens: false,
    toiletsWomens: false,
    kitchenUnits: false,
    kitchenSurfaces: false, // added
    vacuumFloor: false,
    mopFloors: false,
    windows: false,
    toiletRollRefill: false,
    handwashRefill: false,
    paperTowelRefill: false,
    doors: false,
  },
  top: {
    hooverFloors: false,
    mopFloors: false,
    mensToilets: false,
    womensToilets: false,
    kitchenUnits: false,
    kitchenSurfaces: false, // added
    binsEmptied: false,
    windows: false,
    chairs: false,
    desk: false,
    doors: false,
  },
};

const sectionLabels = {
  ground: 'Ground Floor',
  middle: 'Middle Floor',
  top: 'Top Floor',
};

const fieldLabels = {
  ground: {
    canteenFloor: "Canteen Floor",
    tables: "Tables",
    doors: "Doors",
    chairs: "Chairs",
    windows: "Windows",
    surfaces: "Kitchen Surfaces",
    microwave: "Microwave",
    binsEmptied: "Bins Emptied",
    toiletsMens: "Toilets Men's",
    toiletsWomens: "Toilets Women's",
    dryingRoom: "Drying Room",
    toiletRollsRefill: "Toilet Rolls Refill",
    handwashRefill: "Handwash Refill",
  },
  middle: {
    officeFloor: "Office Floor",
    meetingRooms: "Meeting Rooms",
    toiletsMens: "Toilets Men's",
    toiletsWomens: "Toilets Women's",
    kitchenUnits: "Kitchen Units",
    kitchenSurfaces: "Kitchen Surfaces",
    vacuumFloor: "Vacuum Floor",
    mopFloors: "Mop Floors",
    windows: "Windows",
    toiletRollRefill: "Toilet Roll Refill",
    handwashRefill: "Handwash Refill",
    paperTowelRefill: "Paper Towel Refill",
    doors: "Doors",
  },
  top: {
    hooverFloors: "Hoover Floors",
    mopFloors: "Mop Floors",
    mensToilets: "Men's Toilets",
    womensToilets: "Women's Toilets",
    kitchenUnits: "Kitchen Units",
    kitchenSurfaces: "Kitchen Surfaces",
    binsEmptied: "Bins Emptied",
    windows: "Windows",
    chairs: "Chairs",
    desk: "Desk",
    doors: "Doors",
  },
};

const styles = {
  container: {
    maxWidth: 700,
    margin: '40px auto',
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
    padding: 32,
    fontFamily: 'Segoe UI, Arial, sans-serif',
  },
  logo: {
    width: 120,
    height: 'auto',
    display: 'block',
    margin: '0 auto 16px',
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  title: {
    textAlign: 'center',
    fontSize: 32,
    fontWeight: 700,
    color: '#2d3e50',
    marginBottom: 8,
    letterSpacing: 1,
  },
  subtitle: {
    textAlign: 'center',
    fontSize: 18,
    color: '#4a5a6a',
    marginBottom: 24,
    fontWeight: 500,
  },
  section: {
    marginBottom: 32,
    padding: 24,
    borderRadius: 12,
    background: '#f7fafd',
    boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: 600,
    color: '#1a2a3a',
    marginBottom: 16,
    textAlign: 'left',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 12,
    gap: 16,
  },
  label: {
    flex: 1,
    fontSize: 17,
    color: '#2d3e50',
    fontWeight: 500,
  },
  toggle: {
    width: 48,
    height: 28,
    borderRadius: 14,
    background: '#e0e7ef',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background 0.2s',
  },
  toggleActive: {
    background: '#4caf50',
  },
  toggleKnob: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    background: '#fff',
    boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
    position: 'absolute',
    left: 2,
    top: 2,
    transition: 'left 0.2s',
  },
  toggleKnobActive: {
    left: 22,
  },
  button: {
    display: 'block',
    margin: '32px auto 0',
    padding: '12px 36px',
    fontSize: 18,
    fontWeight: 600,
    color: '#fff',
    background: 'linear-gradient(90deg, #4caf50 0%, #2196f3 100%)',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'background 0.2s',
  },
};

export default function FettlerForm() {
  const [form, setForm] = useState(initialState);
  const [comments, setComments] = useState({ ground: '', middle: '', top: '' });
  const pdfRef = useRef();

  const handleToggle = (section, field) => {
    setForm(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: !prev[section][field],
      },
    }));
  };

  const handleCommentChange = (section, value) => {
    setComments(prev => ({ ...prev, [section]: value }));
  };

  const generatePDF = async () => {
    try {
      // Create filename with form name + date
      const currentDate = new Date().toISOString().split('T')[0];
      
      const logoHtml = `<div style='width:100%; text-align:center; margin: 0 0 18px 0;'><img src='${LOGO_URL}' alt='Logo' style='width: 110px; height: auto; border-radius: 14px; box-shadow: 0 1px 4px rgba(0,0,0,0.08); display:inline-block;'/></div>`;
      const pdfHtml = `
        <div style="font-family: 'Segoe UI', Arial, sans-serif; background: #fff; padding: 0; margin: 0;">
          <div style="background: linear-gradient(90deg, #b00 0%, #222 100%); padding: 18px 0 12px 0; text-align: center; border-radius: 0 0 18px 18px; box-shadow: 0 2px 12px rgba(176,0,0,0.10);">
            <div style='font-size: 30px; font-weight: 900; color: #fff; letter-spacing: 1.2px; margin-bottom: 2px;'>Fettlers Form</div>
            <div style='font-size: 18px; color: #fff; font-weight: 500; margin-top: 8px;'>Date: ${currentDate} &nbsp; | &nbsp; Staff: <span style="color:#eee">(Not specified)</span></div>
          </div>
          <div style="padding: 24px;">
            ${Object.keys(sectionLabels).map((section, idx) => `
              <div style='margin-bottom: 32px; background: #fafbfc; border-radius: 12px; padding: 20px; border: 1.5px solid #eee;'>
                <div style='font-size: 20px; font-weight: 700; color: #b00; margin-bottom: 16px; border-bottom: 2px solid #b00; padding-bottom: 8px;'>${sectionLabels[section]}</div>
                <table style='width: 100%; border-collapse: collapse; font-size: 16px; margin-top: 4px;'>
                  <thead>
                    <tr style='background: #b00;'>
                      <th style='padding: 10px 8px; color: #fff; font-size: 15px; font-weight: 700; border-bottom: 2px solid #222;'>Task</th>
                      <th style='padding: 10px 8px; color: #fff; font-size: 15px; font-weight: 700; border-bottom: 2px solid #222;'>Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${Object.keys(fieldLabels[section]).map((field, i) => `
                      <tr style='background: ${i%2===0 ? '#fff' : '#f6f6f6'};'>
                        <td style='padding: 10px 8px; color: #222; border-bottom: 1px solid #eee; font-weight: 500;'>${fieldLabels[section][field]}</td>
                        <td style='text-align: center; padding: 10px 8px; border-bottom: 1px solid #eee; font-size: 20px; font-weight: 700; color: ${form[section][field] ? '#b00' : '#111'};'>${form[section][field] ? '<span style="font-size:22px; color:#b00;">✔️</span>' : '<span style="font-size:22px; color:#111;">✖️</span>'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
                <div style='margin-top: 24px;'>
                  <div style='font-size: 15px; font-weight: 600; color: #b00; margin-bottom: 6px;'>Comments:</div>
                  <div style='min-height: 48px; border: 1.5px solid #b00; border-radius: 8px; background: #fafbfc; padding: 10px 12px; font-size: 15px; color: #222;'>${comments[section] || '<span style="color:#bbb;">(No comments)</span>'}</div>
                </div>
              </div>
            `).join('')}
            <div style='width: 100%; display: flex; flex-direction: column; align-items: center; margin-top: auto;'>
              ${logoHtml}
            </div>
          </div>
        </div>
      `;

      // Generate PDF as blob
      const pdfBlob = await html2pdf()
        .set({
          margin: 0,
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        })
        .from(pdfHtml)
        .outputPdf('blob');

      // Create filename with form name + date
      const fileName = `Fettlers_Form_${currentDate}.pdf`;

      // Save to Supabase
      const formData = {
        formType: 'fettler',
        title: 'Fettlers Form',
        location: 'Not specified',
        createdBy: 'Not specified',
        date: currentDate,
        staffName: 'Not specified',
        formData: form,
        comments: comments
      };

                   await savePDFForm(formData, pdfBlob, fileName);
      
      // Also download locally for user convenience
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert('PDF saved to library and downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div style={styles.container}>
      <div ref={pdfRef}>
        <img src={LOGO_URL} alt="Married to the Mop Logo" style={styles.logo} />
        <div style={styles.title}>Fettlers Audit Form</div>
        <div style={styles.subtitle}>Married to the Mop</div>
        {Object.keys(sectionLabels).map(section => (
          <div key={section} style={styles.section}>
            <div style={styles.sectionTitle}>{sectionLabels[section]}</div>
            {Object.keys(fieldLabels[section]).map(field => (
              <div key={field} style={styles.fieldRow}>
                <span style={styles.label}>{fieldLabels[section][field]}</span>
                <div
                  style={{
                    ...styles.toggle,
                    ...(form[section][field] ? styles.toggleActive : {}),
                  }}
                  onClick={() => handleToggle(section, field)}
                >
                  <div
                    style={{
                      ...styles.toggleKnob,
                      ...(form[section][field] ? styles.toggleKnobActive : {}),
                    }}
                  />
                </div>
                <span style={{ fontWeight: 600, color: form[section][field] ? '#4caf50' : '#e53935' }}>
                  {form[section][field] ? 'Yes' : 'No'}
                </span>
              </div>
            ))}
            {/* Comments input for this floor */}
            <div style={{ marginTop: 18 }}>
              <label style={{ fontWeight: 600, color: '#b00', fontSize: 15, marginBottom: 6, display: 'block' }}>Comments:</label>
              <textarea
                style={{
                  width: '100%',
                  minHeight: 48,
                  border: '1.5px solid #b00',
                  borderRadius: 8,
                  background: '#fafbfc',
                  padding: '10px 12px',
                  fontSize: 15,
                  color: '#222',
                  resize: 'vertical',
                  marginBottom: 0,
                }}
                value={comments[section]}
                onChange={e => handleCommentChange(section, e.target.value)}
                placeholder="Enter comments for this floor..."
              />
            </div>
          </div>
        ))}
      </div>
      <button style={styles.button} onClick={generatePDF}>Download PDF</button>
    </div>
  );
}
